// FontChangeExtension.ts
import { Extension } from "@tiptap/core"
import { Decoration, DecorationSet, EditorView } from "prosemirror-view"
import { Plugin, PluginKey, EditorState, Transaction } from "prosemirror-state"
import { Node as ProseMirrorNode } from "prosemirror-model"
import keywordStyles, { blockKeywords, specialKeywords } from "./keywordStyles"

// Types
interface ExtensionOptions {
  largeFileThreshold: number
}

interface DecorationResult {
  decorations: Decoration[]
  insideBlock: string | null
}

interface LineDecorationsResult extends DecorationResult {
  emptyLineCount: number
}

interface GeneralKeywordResult {
  decorations: Decoration[]
  lineContainsKeyword: boolean
}

interface PluginMeta {
  decorations?: DecorationSet
}

// Constants
const UPDATE_DEBOUNCE = 300 // Debounce time in milliseconds

// Plugin key for syntax highlighting
const syntaxHighlightPluginKey = new PluginKey<DecorationSet>("syntaxHighlight")

// Regex patterns
const propertyRegex = /\[\[(.*?)\]\]/g
const angleBracketRegex = /<<([^>>]*?)>>/g

// RECODE-assignment LHS: optional leading whitespace + optional `RECODE`, target (bare ID or <<...>>),
// optional `::<<row>>`, then a single `=`. `(?!=)` excludes `==` so IF expressions don't false-match.
// Anchored at line start, so it won't fire mid-line.
// Group 1 captures everything before the target (whitespace + optional RECODE) — used to compute target offset.
const recodeTargetRegex = /^(\s*(?:RECODE\s+)?)(<<[^<>]+?>>|[A-Za-z_][\w]*)(\s*::\s*<<[^<>]+?>>)?(?=\s*=(?!=))/

// Sorted block keywords by length (longer first)
const sortedBlockKeywords: string[] = Object.keys(blockKeywords).sort(
  (a: string, b: string) => b.length - a.length
)

// Cache for memoization
const memoizedResults = new Map<string, GeneralKeywordResult>()

// Debounce function with a cancel handle, so pending timers can be cleared
// when the view is destroyed or a synchronous update supersedes them
interface DebouncedFunction<T extends (...args: any[]) => void> { // eslint-disable-line @typescript-eslint/no-explicit-any
  (...args: Parameters<T>): void
  cancel: () => void
}

const debounce = <T extends (...args: any[]) => void>( // eslint-disable-line @typescript-eslint/no-explicit-any
  fn: T,
  delay: number
): DebouncedFunction<T> => {
  let timeoutId: NodeJS.Timeout | undefined
  const debounced = ((...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }) as DebouncedFunction<T>
  debounced.cancel = () => clearTimeout(timeoutId)
  return debounced
}

/**
 * Handles special keywords within a line.
 * @param line - The text of the line
 * @param linePos - The position of the line in the document
 * @returns An array of decorations for the special keywords
 */
const handleSpecialKeywords = (line: string, linePos: number): Decoration[] => {
  const decorations: Decoration[] = []

  // Find all angle bracket ranges in the line
  const angleBracketRanges: Array<{start: number, end: number}> = []
  let angleBracketMatch: RegExpExecArray | null
  const angleBracketRegexForRanges = /<<([^>>]*?)>>/g
  
  while ((angleBracketMatch = angleBracketRegexForRanges.exec(line)) !== null) {
    angleBracketRanges.push({
      start: angleBracketMatch.index,
      end: angleBracketMatch.index + angleBracketMatch[0].length
    })
  }

  specialKeywords.forEach((keyword: string) => {
    // Allow `(` as a boundary so function-call forms like PRIORITY(...), RANDOM(...), COUNT(...) match.
    const regex = new RegExp(`\\b${keyword}(?=\\s|$|\\()`, "g")
    let match: RegExpExecArray | null

    while ((match = regex.exec(line)) !== null) {
      // Skip special keywords if they're at the start of the line (ignoring leading whitespace)
      const skipAtStartKeywords = ["IN", "NOT", "ANY OF", "AND", "OR", "AS", "IS", "IS NOT", "WHERE", "COUNT"]
      if (
        skipAtStartKeywords.includes(keyword) &&
        line.trim().startsWith(keyword) &&
        match.index === line.indexOf(line.trim())
      ) {
        continue
      }

      // Skip if the keyword is inside angle brackets
      const keywordStart = match.index
      const keywordEnd = match.index + keyword.length
      const isInsideAngleBrackets = angleBracketRanges.some(range => 
        keywordStart >= range.start && keywordEnd <= range.end
      )
      
      if (isInsideAngleBrackets) {
        continue
      }

      const startKeyword = linePos + match.index
      const endKeyword = startKeyword + keyword.length

      decorations.push(
        Decoration.inline(startKeyword, endKeyword, {
          class: keywordStyles[keyword]?.keywordClass || "highlight-as",
        })
      )
    }
  })

  return decorations
}

/**
 * Handles decorations for block keywords in a line.
 * @param line - The text of the line
 * @param linePos - The position of the line in the document
 * @returns An object containing decorations and the current block class
 */
const handleBlockKeyword = (
  line: string,
  linePos: number
): DecorationResult => {
  const trimmedLine = line.trim()
  const blockKeywordMatch = sortedBlockKeywords.find((keyword: string) =>
    trimmedLine.startsWith(keyword)
  )
  if (blockKeywordMatch) {
    // Calculate the actual position accounting for leading whitespace
    const leadingWhitespace = line.length - line.trimStart().length
    const startKeyword = linePos + leadingWhitespace
    const endKeyword = startKeyword + blockKeywordMatch.length
    const decorations: Decoration[] = []

    // For NOTE and TEXT blocks, apply styling to the entire line
    if (blockKeywordMatch === "NOTE" || blockKeywordMatch === "TEXT") {
      // Apply bold to the keyword
      decorations.push(
        Decoration.inline(startKeyword, endKeyword, { class: "highlight-bold" })
      )
      // For `TEXT <label>: <content>`, color the label blue (highlight-item) — matching Q/QUESTION.
      // The colon and everything after it stay in the block style. Without a colon, fall back
      // to the original behavior (entire post-keyword span styled as the block class).
      const afterKeyword = line.substring(endKeyword - linePos)
      const labelColonMatch =
        blockKeywordMatch === "TEXT" ? /^(\s+[^:]*?)(:.*)$/.exec(afterKeyword) : null
      if (labelColonMatch) {
        const labelEnd = endKeyword + labelColonMatch[1].length
        decorations.push(
          Decoration.inline(endKeyword, labelEnd, { class: "highlight-item" })
        )
        decorations.push(
          Decoration.inline(labelEnd, linePos + line.length, {
            class: blockKeywords[blockKeywordMatch],
          })
        )
      } else if (endKeyword < linePos + line.length) {
        decorations.push(
          Decoration.inline(endKeyword, linePos + line.length, {
            class: blockKeywords[blockKeywordMatch],
          })
        )
      }
    } else {
      // For other block keywords, handle normally
      decorations.push(
        Decoration.inline(startKeyword, endKeyword, { class: "highlight-bold" })
      )
      decorations.push(
        Decoration.inline(endKeyword, linePos + line.length, {
          class: blockKeywords[blockKeywordMatch],
        })
      )
    }
    return { decorations, insideBlock: blockKeywords[blockKeywordMatch] }
  }
  return { decorations: [], insideBlock: null }
}

/**
 * Memoized function for handling general keywords.
 * @param line - The text of the line
 * @param linePos - The position of the line in the document
 * @returns An object containing decorations and whether the line contains a keyword
 */
const handleGeneralKeyword = (
  line: string,
  linePos: number
): GeneralKeywordResult => {
  const cacheKey = `${linePos}:${line}`
  if (memoizedResults.has(cacheKey)) {
    return memoizedResults.get(cacheKey)!
  }

  const decorations: Decoration[] = []
  let lineContainsKeyword = false
  const keywords: string[] = Object.keys(keywordStyles).sort(
    (a: string, b: string) => b.split(" ").length - a.split(" ").length
  )

  // Check Q/QUESTION without colon first
  if (/^(Q|QUESTION)\s+[^:]*$/.test(line.trim())) {
    decorations.push(
      Decoration.inline(linePos, linePos + line.length, {
        class: "highlight-default-red",
      })
    )
    const result: GeneralKeywordResult = {
      decorations,
      lineContainsKeyword: true,
    }
    memoizedResults.set(cacheKey, result)
    return result
  }

  for (const keyword of keywords) {
    const { keywordClass, contentClass } = keywordStyles[keyword]
    let regex: RegExp
    if (keyword === "Q" || keyword === "QUESTION") {
      regex = new RegExp(`^(${keyword})(\\s+[^:]*)(:.*)`, "g")
    } else if (keyword === "DATA") {
      regex = new RegExp(`^(DATA)(\\s+.*)?`, "g")
    } else {
      regex = new RegExp(`\\b(${keyword})(?=\\s|$)(\\s.*)?`, "g")
    }

    let match: RegExpExecArray | null
    while ((match = regex.exec(line)) !== null) {
      // Only treat as a primary, line-leading keyword if it actually starts the line (after
      // optional whitespace). Without this, mid-line operators like `AND` / `OR` / `IS` / `COUNT`
      // (which are also entries in keywordStyles) claim lineContainsKeyword and suppress the
      // block-continuation styling that body lines of a RECODE/LIST block depend on.
      // handleSpecialKeywords still highlights these mid-line.
      if (!/^\s*$/.test(line.substring(0, match.index))) continue
      lineContainsKeyword = true
      const startKeyword = linePos + match.index
      const endKeyword = startKeyword + match[1].length

      if (keyword === "Q" || keyword === "QUESTION") {
        // Use the correct keywordClass from keywordStyles
        decorations.push(
          Decoration.inline(startKeyword, endKeyword, { class: keywordClass })
        )

        if (match[2]) {
          // Highlight the text before colon
          const labelStart = endKeyword
          const labelEnd = labelStart + match[2].length
          decorations.push(
            Decoration.inline(labelStart, labelEnd, { class: "highlight-item" })
          )

          // Make colon and everything after it black
          if (match[3]) {
            const colonStart = labelEnd
            const colonEnd = linePos + line.length
            decorations.push(
              Decoration.inline(colonStart, colonEnd, {
                class: "highlight-text-black",
              })
            )
          }
        }
      } else {
        // Handle other keywords normally
        decorations.push(
          Decoration.inline(startKeyword, endKeyword, { class: keywordClass })
        )

        if (match[2]) {
          const startContent = endKeyword
          const endContent = linePos + match.index + match[0].length
          decorations.push(
            Decoration.inline(startContent, endContent, { class: contentClass })
          )
        }
      }
    }
    if (lineContainsKeyword) break
  }

  const result: GeneralKeywordResult = {
    decorations,
    lineContainsKeyword,
  }
  memoizedResults.set(cacheKey, result)
  return result
}

/**
 * Handles decorations for a line.
 * @param line - The text of the line
 * @param linePos - The position of the line in the document
 * @param insideBlock - The current block class if inside a block
 * @param emptyLineCount - Current count of empty lines
 * @returns An object containing decorations and the current block class
 */
const handleLineDecorations = (
  line: string,
  linePos: number,
  insideBlock: string | null,
  emptyLineCount: number
): LineDecorationsResult => {
  let decorations: Decoration[] = []

  const isLineEmpty = typeof line === "string" && (!line || /^\s*$/.test(line))
  let currentEmptyLineCount = isLineEmpty ? (emptyLineCount || 0) + 1 : 0

  const isQTextLine = /^(Q|QUESTION)\s/.test(line.trim())
  const isBlockKeyword = sortedBlockKeywords.some((keyword: string) =>
    line.trim().startsWith(keyword)
  )

  // Always use threshold of 1
  const threshold = 1

  // Check for block keywords (like NOTE and TEXT)
  const { decorations: blockDecorations, insideBlock: newInsideBlock } =
    handleBlockKeyword(line, linePos)

  // Check if this line starts with a general keyword (which should break blocks)
  // No keywords are excluded - all keywords in keywordStyles can break blocks
  const keywords: string[] = Object.keys(keywordStyles)
  const startsWithGeneralKeyword = keywords.some((keyword: string) => {
    if (keyword === "Q" || keyword === "QUESTION") {
      return /^(Q|QUESTION)\s/.test(line.trim())
    } else {
      return new RegExp(`^${keyword}\\b`, "i").test(line.trim())
    }
  })

  // Check if this is a NOTE or TEXT block that should skip other processing
  // But only if the line doesn't start with a general keyword
  const isSpecialBlock =
    (newInsideBlock === "highlight-note" ||
      newInsideBlock === "highlight-text-black" ||
      insideBlock === "highlight-note" ||
      insideBlock === "highlight-text-black") &&
    !startsWithGeneralKeyword

  if (isSpecialBlock) {
    // For NOTE and TEXT blocks, return early with only block styling
    const specialBlockDecorations = blockDecorations.slice()

    // If we're inside a block but this line doesn't start with a block keyword,
    // we need to add decoration for the entire line
    if (insideBlock && !newInsideBlock) {
      specialBlockDecorations.push(
        Decoration.inline(linePos, linePos + line.length, {
          class: insideBlock,
        })
      )
    }

    return {
      decorations: specialBlockDecorations,
      insideBlock: newInsideBlock || insideBlock,
      emptyLineCount: currentEmptyLineCount,
    }
  }

  let lineContainsKeyword = false

  // For non-NOTE/TEXT lines, apply all decorations normally
  const { decorations: generalDecorations, lineContainsKeyword: hasKeyword } =
    handleGeneralKeyword(line, linePos)
  lineContainsKeyword = hasKeyword
  decorations = decorations.concat(generalDecorations)

  // Apply default red highlighting if needed
  if (
    line.trim() &&
    !lineContainsKeyword &&
    !insideBlock &&
    !isQTextLine &&
    !isBlockKeyword
  ) {
    decorations.push(
      Decoration.inline(linePos, linePos + line.length, {
        class: "highlight-default-red",
      })
    )
  }

  // Apply special keywords
  decorations = decorations.concat(handleSpecialKeywords(line, linePos))

  // Add block decorations
  decorations = decorations.concat(blockDecorations)

  let actualNewInsideBlock = newInsideBlock

  if (isBlockKeyword || isQTextLine) {
    currentEmptyLineCount = 0
    actualNewInsideBlock = isQTextLine
      ? "text-block"
      : blockKeywords[
          sortedBlockKeywords.find((keyword: string) =>
            line.trim().startsWith(keyword)
          )!
        ]
  }

  if (isLineEmpty) {
    if (currentEmptyLineCount > threshold) {
      decorations.push(
        Decoration.inline(linePos, linePos + (line.length || 1), {
          class: "highlight-default-red",
        })
      )
      insideBlock = null
      actualNewInsideBlock = null
    } else if (insideBlock) {
      decorations.push(
        Decoration.inline(linePos, linePos + (line.length || 1), {
          class: insideBlock,
        })
      )
    }
  } else if (!lineContainsKeyword && !actualNewInsideBlock) {
    if (currentEmptyLineCount > threshold || !insideBlock) {
      decorations.push(
        Decoration.inline(linePos, linePos + line.length, {
          class: "highlight-default-red",
        })
      )
    } else if (insideBlock) {
      decorations.push(
        Decoration.inline(linePos, linePos + line.length, {
          class:
            insideBlock === "text-block" ? "highlight-text-black" : insideBlock,
        })
      )
    }
  }

  const shouldResetBlock = currentEmptyLineCount > threshold

  return {
    decorations,
    insideBlock: shouldResetBlock ? null : actualNewInsideBlock || insideBlock,
    emptyLineCount: currentEmptyLineCount,
  }
}

const FontChangeExtension = Extension.create<ExtensionOptions>({
  name: "syntaxHighlighting",

  addOptions(): ExtensionOptions {
    return {
      largeFileThreshold: 150000, // characters
    }
  },

  addProseMirrorPlugins() {
    const { largeFileThreshold } = this.options

    const updateDecorations = (doc: ProseMirrorNode): DecorationSet => {
      const decos: Decoration[] = []
      let insideBlock: string | null = null
      let emptyLineCount = 0
      let previousNodeWasEmpty = false
      let skipNextEmptyLineCount = false // Flag to skip empty line counting after a remark
      // Remark state carried across text-node visits within a single paragraph. Inline formatting
      // (Bold/Italic) splits a paragraph into multiple text nodes, so a remark like
      // `# normal **bold** rest` arrives as three text nodes — only the first starts with `#`.
      // Without propagation, the bold span and the trailing text would render as default-red.
      // Reset to false at every paragraph boundary (see the non-text branch below).
      let inRemarkParagraph = false

      doc.descendants((node: ProseMirrorNode, pos: number) => {
        if (node.isText) {
          const text = node.text || ""
          const lines = text.split(/\r?\n/)
          let currentPos = pos

          lines.forEach((line: string, lineIndex: number) => {
            const lineFrom = currentPos
            const lineTo = lineFrom + line.length
            // First line of this text node inherits remark state from prior text nodes in the
            // same paragraph; subsequent lines (after a `\n` inside one text node) are new lines
            // and only stay remark-styled if they themselves start with `#` / `REMARK`.
            if (lineIndex > 0) inRemarkParagraph = false
            const isRemark =
              inRemarkParagraph ||
              line.trim().startsWith("#") ||
              line.trim().startsWith("REMARK")

            if (isRemark) {
              // Handle remark line - add decoration but don't affect empty line count
              decos.push(
                Decoration.inline(lineFrom, lineTo, {
                  class: "highlight-remark",
                })
              )
              // Persist for any subsequent text nodes in the same paragraph (bold/italic spans).
              inRemarkParagraph = true
              // Set flag to skip next empty line count after a remark
              skipNextEmptyLineCount = true
            } else if (!line.trim()) {
              // Empty line - only count if we're not skipping
              if (!skipNextEmptyLineCount) {
                emptyLineCount = previousNodeWasEmpty ? emptyLineCount + 1 : 1
                previousNodeWasEmpty = true

                if (emptyLineCount > 1) {
                  insideBlock = null
                }
              } else {
                // If we're skipping due to a previous remark, reset the flag
                skipNextEmptyLineCount = false
              }

              decos.push(
                Decoration.inline(lineFrom, lineTo, {
                  class:
                    emptyLineCount > 1
                      ? "highlight-default-red"
                      : insideBlock || "highlight-default-red",
                })
              )
            } else {
              // Non-empty, non-remark line
              skipNextEmptyLineCount = false
              emptyLineCount = 0
              previousNodeWasEmpty = false
              inRemarkParagraph = false

              const result = handleLineDecorations(
                line,
                lineFrom,
                insideBlock,
                emptyLineCount
              )
              decos.push(...result.decorations)
              insideBlock = result.insideBlock
              emptyLineCount = result.emptyLineCount

              let match: RegExpExecArray | null
              while ((match = propertyRegex.exec(line)) !== null) {
                decos.push(
                  Decoration.inline(
                    lineFrom + match.index,
                    lineFrom + match.index + match[0].length,
                    { class: "highlight-validate" }
                  )
                )
              }
              while ((match = angleBracketRegex.exec(line)) !== null) {
                decos.push(
                  Decoration.inline(
                    lineFrom + match.index,
                    lineFrom + match.index + match[0].length,
                    { class: "highlight-validate" }
                  )
                )
              }
              // Style bare RECODE assignment targets (e.g. `QAGE` in `QAGE::<<row>> = ...`)
              // the same green as <<Industry Group>>-style targets, regardless of whether the
              // line is the inline form (with leading RECODE) or a body continuation line.
              const recodeMatch = recodeTargetRegex.exec(line)
              if (recodeMatch && recodeMatch[2]) {
                const targetStart = lineFrom + recodeMatch[1].length
                const targetEnd = targetStart + recodeMatch[2].length
                decos.push(
                  Decoration.inline(targetStart, targetEnd, {
                    class: "highlight-validate",
                  })
                )
              }
            }

            currentPos = lineTo + 1
          })
        } else {
          // New paragraph (or other block boundary): a remark only spans a single line,
          // so end any in-progress remark before processing the next paragraph's text.
          inRemarkParagraph = false
          // Only count non-text nodes as empty if we're not skipping
          if (!skipNextEmptyLineCount) {
            if (!previousNodeWasEmpty) {
              emptyLineCount = 1
              previousNodeWasEmpty = true
            } else {
              emptyLineCount++
            }

            if (emptyLineCount > 1) {
              insideBlock = null
            }
          } else {
            // Reset the skip flag
            skipNextEmptyLineCount = false
          }
        }
        return true
      })

      return DecorationSet.create(doc, decos)
    }

    // Read the state at fire time, never from a captured snapshot: the doc may
    // have changed (or the view been destroyed) while the timer was pending, and
    // dispatching a transaction built from a stale state throws
    // "RangeError: Applying a mismatched transaction".
    const debouncedUpdateDecorations = debounce((view: EditorView) => {
      if (view.isDestroyed) return
      const { state } = view
      const decorations = updateDecorations(state.doc)
      view.dispatch(state.tr.setMeta(syntaxHighlightPluginKey, { decorations }))
    }, UPDATE_DEBOUNCE)

    return [
      new Plugin<DecorationSet>({
        key: syntaxHighlightPluginKey,
        state: {
          init(_, { doc }: { doc: ProseMirrorNode }): DecorationSet {
            return updateDecorations(doc)
          },
          apply(tr: Transaction, oldState: DecorationSet): DecorationSet {
            const meta = tr.getMeta(syntaxHighlightPluginKey) as
              | PluginMeta
              | undefined
            if (meta && meta.decorations) {
              return meta.decorations
            }

            // Check if this is a Yjs sync transaction
            const isYjsSync = tr.getMeta("y-sync$")

            // If it's a Yjs sync and content appeared, recalculate decorations
            if (isYjsSync && tr.docChanged) {
              return updateDecorations(tr.doc)
            }

            return oldState.map(tr.mapping, tr.doc)
          },
        },
        props: {
          decorations(state: EditorState): DecorationSet {
            return this.getState(state)!
          },
        },
        view() {
          return {
            update: (view: EditorView, prevState: EditorState) => {
              if (view.state.doc !== prevState.doc) {
                const docSize = view.state.doc.content.size
                if (docSize > largeFileThreshold) {
                  debouncedUpdateDecorations(view)
                } else {
                  // A pending debounce from when the doc was still above the
                  // threshold is superseded by this synchronous update
                  debouncedUpdateDecorations.cancel()
                  const decorations = updateDecorations(view.state.doc)
                  view.dispatch(
                    view.state.tr.setMeta(syntaxHighlightPluginKey, {
                      decorations,
                    })
                  )
                }
              }
            },
            destroy: () => {
              debouncedUpdateDecorations.cancel()
            },
          }
        },
      }),
    ]
  },
})

export default FontChangeExtension
